import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Icon, Float } from "uikit";
import { isEqual } from "lodash";

import { Language } from "../../../../../services";
import TaxiService from "../../../../../services/TaxiService";
import { useTypedSelector } from "../../../../../redux/store";
import {
	defaultParkingExecutorFilters,
	sanitizeParkingWidgetFilter,
} from "../../../../../redux/reducers/Orders/Parking/filter";
import useParkingActions from "../../hooks/useParkingActions";
import {
	useCompanyIdsDecoder,
	useTaxiServiceIdsDecoder,
} from "../../../../../hooks";

import Popover from "./components/Popover";

const Filter: React.FC<Filter.Props> = ({
	taxiServiceData,
	taxiServiceIdsPosition,
	language,
}) => {
	const { onChangeFilter } = useParkingActions();

	const { criteria: rawFilters } = useTypedSelector(
		(state) => state.orders.parking.filter,
	);
	const filters = useMemo(
		() => sanitizeParkingWidgetFilter(rawFilters),
		[rawFilters],
	);

	const allTaxiServiceIds = useTaxiServiceIdsDecoder();
	const allCompanyIds = useCompanyIdsDecoder();

	const taxiServiceIds = useMemo(
		() => allTaxiServiceIds(["all"], ["all"], { isOnlyAccess: true }),
		[allTaxiServiceIds],
	);
	const companyIds = useMemo(
		() => allCompanyIds(["all"], { isOnlyAccess: true }),
		[allCompanyIds],
	);

	const isDefaultExecutorFilters = useMemo(() => {
		const sorted = (value: string[] | undefined) =>
			[...(value ?? [])].sort();

		return (
			isEqual(
				sorted(filters.statuses),
				sorted(defaultParkingExecutorFilters.statuses),
			) &&
			isEqual(
				sorted(filters.online),
				sorted(defaultParkingExecutorFilters.online),
			) &&
			isEqual(
				sorted(filters.workingStatus),
				sorted(defaultParkingExecutorFilters.workingStatus),
			) &&
			filters.isYourOrderStatus ===
				defaultParkingExecutorFilters.isYourOrderStatus
		);
	}, [filters]);

	const isFilterActive = useMemo(() => {
		if (!isDefaultExecutorFilters) return true;
		if (companyIds?.length !== filters.companyIds?.length) return true;
		if (taxiServiceIds?.length !== filters.taxiServiceIds?.length)
			return true;
		if (filters.executorGroupIds?.length > 0) return true;
		return false;
	}, [
		companyIds?.length,
		filters.companyIds?.length,
		filters.executorGroupIds?.length,
		filters.taxiServiceIds?.length,
		isDefaultExecutorFilters,
		taxiServiceIds?.length,
	]);

	const [isButtonLocked, setIsButtonLocked] = useState(false);
	const [openAdditionalFilters, setOpenAdditionalFilters] = useState(false);
	const [showModalPropsForExecutorGroup, setShowModalPropsForExecutorGroup] =
		useState<boolean>(false);

	const isActiveShowMultiModal = useMemo(
		() => showModalPropsForExecutorGroup,
		[showModalPropsForExecutorGroup],
	);

	const buttonOnClick = useCallback(() => {
		openAdditionalFilters
			? undefined
			: !isButtonLocked && setOpenAdditionalFilters(true);
	}, [isButtonLocked, openAdditionalFilters]);

	const buttonOnMouseDown = useCallback(
		(event) => {
			event.preventDefault();
			if (openAdditionalFilters) setIsButtonLocked(true);
		},
		[openAdditionalFilters],
	);

	const popoverOnClose = useCallback(() => {
		if (!isActiveShowMultiModal) setOpenAdditionalFilters(false);
	}, [isActiveShowMultiModal]);

	useEffect(() => {
		if (filters !== rawFilters) onChangeFilter(filters);
	}, [filters, onChangeFilter, rawFilters]);

	useEffect(() => {
		const unblockHandler = () => {
			setIsButtonLocked(false);
		};

		document.addEventListener("click", unblockHandler);

		return () => {
			document.removeEventListener("click", unblockHandler);
		};
	}, []);

	return (
		<>
			<Float.Tracker id="filters">
				<Button.Button
					style={
						isFilterActive
							? { backgroundColor: "rgb(109,190,255)" }
							: {}
					}
					variant={isFilterActive ? "primary" : "secondary"}
					icon={<Icon id="filters" size={20} />}
					onClick={buttonOnClick}
					onMouseDown={buttonOnMouseDown}
				/>
			</Float.Tracker>

			<Popover
				taxiServiceData={taxiServiceData}
				taxiServiceIdsPosition={taxiServiceIdsPosition}
				language={language}
				filter={filters}
				setFilter={onChangeFilter}
				open={openAdditionalFilters}
				onClose={popoverOnClose}
				setShowModalPropsForExecutorGroup={
					setShowModalPropsForExecutorGroup
				}
			/>
		</>
	);
};

declare namespace Filter {
	interface Props {
		taxiServiceData: TaxiService.Model[] | undefined;
		taxiServiceIdsPosition: number[];
		language: Language;
	}
}

export default Filter;
